import GenericApi from "@/api/genericApi";
import {
  BalanceQueryKey,
  CryptoAccountQueryKey,
  CryptoDepositQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import {
  CryptoAccount,
  CryptoDeposit,
  CryptoDepositResponse,
} from "@/types/BankSettings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useState } from "react";
import { ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { Balance } from "@/types/Balance";
import { isBitramp } from "@/utils/utils";

interface WidthdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DepositModal: FC<WidthdrawModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [selectedCurrency, setSelectedCurrency] = useState<string>("-");
  const [selectedWallet, setSelectedWallet] = useState<string>("-");
  const [selectedNetwork, setSelectedNetwork] = useState<string>("-");
  const [QRCode, setQRCode] = useState<boolean>(false);
  const [selectedPercent, setSelectedPercent] = useState(50);
  const [amount, setAmount] = useState<string | "">("");

  const [percentAmount, setPercentAmount] = useState<number>(0);

  const { data: cryptoAccounts } = useQuery<ApiResponse<CryptoAccount[]>>({
    queryKey: [CryptoAccountQueryKey],
    queryFn: async () =>
      GenericApi.get<CryptoAccount[]>(`${ApiRoutes.CRYPTOACCOUNT}`),
  });

  const updateCryptoDepositMutation = useMutation<
    ApiResponse<CryptoDepositResponse>,
    ApiError<ValidationErrors>,
    CryptoDeposit
  >({
    mutationFn: (data) =>
      GenericApi.post<CryptoDepositResponse>(ApiRoutes.CRYPTOWITHDRAW, data),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);

      await queryClient.invalidateQueries({
        queryKey: [CryptoDepositQueryKey],
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const { data: balance } = useQuery<ApiResponse<Balance[]>>({
    queryFn: async () => GenericApi.get<Balance[]>(ApiRoutes.GET_BALANCE),
    queryKey: [BalanceQueryKey],
  });

  const selectedBalance = balance?.data?.find(
    (item) => item.currency === selectedCurrency
  );

  const isAmountValid =
    selectedBalance && amount !== ""
      ? Number(amount) <= Number(selectedBalance.balance)
      : false;

  useEffect(() => {
    if (selectedBalance) {
      setPercentAmount(
        Number(selectedBalance.balance) * (selectedPercent / 100)
      );
    }
  }, [selectedBalance]);

  const handlePercentChange = (percent: number) => {
    setSelectedPercent(percent);
    if (selectedBalance) {
      setPercentAmount(Number(selectedBalance.balance) * (percent / 100));
    }
  };

  const handleCryptoDeposit = () => {
    if (!amount) {
      CustomLogger.error("Please select a wallet and enter an amount.");
      return;
    }

    const payload = {
      currency: selectedCurrency,
      network:
        selectedCurrency === "USDT" || selectedCurrency === "USTC"
          ? selectedNetwork
          : "",
      amount: Number(amount),
      from_crypto_account: selectedWallet,
    };

    updateCryptoDepositMutation.mutate(payload);
  };

  const handleCloseModal = (e: React.MouseEvent) => {
    // Închide doar atunci când se face click pe fundal, nu pe conținutul modalei
    if (e.target === e.currentTarget) {
      onClose();
      setQRCode(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleCloseModal}
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-[30rem] h-auto ${
          isBitramp() ? "bg-[#2B2B2B] border border-[#2B2B2B]" : "bg-white"
        } rounded-[1.5rem] border text-center relative`}
      >
        <button
          onClick={() => {
            onClose();
            setQRCode(false);
          }}
          className="absolute top-4 right-4"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="19"
            height="19"
            viewBox="0 0 19 19"
            fill="none"
          >
            <path
              d="M17 17L2 2"
              stroke={isBitramp() ? "#fff" : "#202A43"}
              stroke-width="3"
              stroke-linecap="round"
            />
            <path
              d="M2 17L17 2"
              stroke={isBitramp() ? "#fff" : "#202A43"}
              stroke-width="3"
              stroke-linecap="round"
            />
          </svg>
        </button>

        <div className="text-[#05004E] pt-[2.5rem] text-center text-[1.5rem] font-[700]">
          {QRCode ? (
            <div className="w-full items-center flex justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="86"
                height="129"
                viewBox="0 0 86 129"
                fill="none"
              >
                <g clip-path="url(#clip0_3308_8423)">
                  <path
                    d="M55.8233 59.4141C53.8082 61.5311 53.0487 64.5741 54.2431 67.3269C55.658 70.5858 63.2529 75.178 66.2724 77.5605C78.7122 87.362 84.1572 98.3733 85.6211 114.242C85.9028 117.285 86.4602 120.464 85.2536 123.396C83.9735 126.494 80.9845 128.698 77.6648 129.006H8.1592C2.3589 128.025 -0.20744 123.717 0.0130569 118.007C0.625549 102.317 5.89298 89.0162 17.9836 78.9431C21.487 76.0236 29.431 71.4253 31.4645 67.8022C33.6817 63.8519 31.5564 60.5744 28.5797 57.9759C25.9031 55.6367 22.5711 53.6677 19.7475 51.4457C7.31396 41.6874 1.85666 30.5958 0.398927 14.7578C0.123305 11.7643 -0.421813 8.64115 0.705173 5.74636C1.94241 2.58 4.99874 0.308612 8.3552 0H77.8608C81.1438 0.419713 84.096 2.62938 85.3148 5.74636C86.4418 8.64115 85.8967 11.7643 85.6211 14.7578C84.1633 30.6082 78.6999 41.6688 66.2724 51.4395C63.0569 53.9701 58.5673 56.5255 55.8233 59.4079V59.4141ZM42.1709 89.0717C41.0929 89.2569 39.6658 90.337 38.7899 91.0036C29.9272 97.7746 21.8361 105.675 12.9734 112.465C11.0073 114.014 11.5095 117.094 14.0514 117.52H71.9747C74.7371 116.81 74.9392 114.217 72.9486 112.366C71.4909 111.008 69.5922 109.712 68.0425 108.409C60.76 102.299 53.4652 96.1945 46.146 90.1148C44.8413 89.3742 43.7327 88.7878 42.1709 89.0594V89.0717Z"
                    fill="#D2D2D2"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_3308_8423">
                    <rect width="86" height="129" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>
          ) : (
            <div className={`${isBitramp() ? "text-white" : "text-[#05004E]"}`}>
              Withdraw
            </div>
          )}
        </div>

        <div className="w-full text-[#737791] text-[1.2rem] pt-[1rem] font-[500] text-center">
          {QRCode ? (
            <div
              className={`${isBitramp() ? "text-[#9F9F9F]s" : "text-[#737791]"}`}
            >
              Your withdrawal
              <br /> is in pending
            </div>
          ) : (
            <div
              className={`${isBitramp() ? "text-[#9F9F9F]" : "text-[#737791]"}`}
            >
              Please add your details to continue
            </div>
          )}
        </div>

        {QRCode ? (
          <div></div>
        ) : (
          <div className="mt-6 flex flex-col items-center">
            <div className="relative w-[22rem] mt-[1rem]">
              <select
                className={`w-full p-3 border ${
                  isBitramp()
                    ? "bg-[#2B2B2B] border border-[#464646] text-white focus:border-[#464646] focus:ring-0"
                    : "bg-white border-[#D7D7D7] text-black"
                } rounded-[0.7rem] appearance-none`}
                value={selectedWallet}
                onChange={(e) => {
                  const selectedWalletId = e.target.value;
                  setSelectedWallet(selectedWalletId);

                  const wallet = cryptoAccounts?.data?.find(
                    (cryptoAccount) =>
                      cryptoAccount.id === Number(selectedWalletId)
                  );

                  if (wallet) {
                    setSelectedCurrency(wallet.currency);
                  }
                }}
              >
                <option value="-" disabled>
                  Select wallet
                </option>
                {cryptoAccounts?.data?.map((cryptoAccount) => (
                  <option key={cryptoAccount.id} value={cryptoAccount.id}>
                    {cryptoAccount.name}
                  </option>
                ))}
              </select>

              <div className="absolute top-1/2 right-[0.59rem] transform -translate-y-1/2 pointer-events-none rotate-180">
                <svg
                  width="18"
                  height="16"
                  viewBox="0 0 13 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`transition-transform duration-300 ${
                    isOpen ? "rotate-180" : "rotate-0"
                  }`}
                >
                  <path
                    d="M7.35044 9.6252C6.95955 10.2571 6.04045 10.2571 5.64956 9.6252L0.948823 2.02607C0.536717 1.35987 1.0159 0.5 1.79926 0.5L11.2007 0.500001C11.9841 0.500001 12.4633 1.35987 12.0512 2.02607L7.35044 9.6252Z"
                    fill={isBitramp() ? "#fff" : "#222B45"}
                  />
                </svg>
              </div>
            </div>

            {selectedCurrency === "USDT" || selectedCurrency === "USTC" ? (
              <div className="relative w-[22rem]">
                <select
                  className="w-full p-3 mt-[1rem] border border-[#D7D7D7] rounded-[0.7rem] text-black bg-white appearance-none"
                  value={selectedNetwork}
                  onChange={(e) => setSelectedNetwork(e.target.value)}
                >
                  <option value="-" disabled>
                    Select network
                  </option>
                  <option value="ERC20">ERC20</option>
                  <option value="TRC20">TRC20</option>
                </select>

                <div className="absolute mt-[0.5rem] top-1/2 right-[0.66rem] transform -translate-y-1/2 pointer-events-none rotate-180">
                  <svg
                    width="17.8"
                    height="15.8"
                    viewBox="0 0 13 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`transition-transform duration-300 ${
                      isOpen ? "rotate-180" : "rotate-0"
                    }`}
                  >
                    <path
                      d="M7.35044 9.6252C6.95955 10.2571 6.04045 10.2571 5.64956 9.6252L0.948823 2.02607C0.536717 1.35987 1.0159 0.5 1.79926 0.5L11.2007 0.500001C11.9841 0.500001 12.4633 1.35987 12.0512 2.02607L7.35044 9.6252Z"
                      fill="#222B45"
                    />
                  </svg>
                </div>
              </div>
            ) : (
              <div></div>
            )}

            <div className="relative w-[22rem] pt-[1rem]">
              <input
                type="text"
                placeholder={
                  selectedBalance?.balance
                    ? `Max: ${selectedBalance.balance.slice(0, 10)}`
                    : "0"
                }
                className={`w-full p-3 pr-10 ${
                  isBitramp()
                    ? "bg-[#2B2B2B] border border-[#464646] text-white focus:border-[#464646] focus:ring-0 placeholder:text-[rgba(115, 119, 145, 0.34)]"
                    : "border border-[#D7D7D7] placeholder:text-[#D3D3D3]"
                } rounded-[0.7rem] `}
                onChange={(e) => {
                  let val = e.target.value.replace(",", ".");

                  if (val.startsWith("00")) {
                    val = "0.0";
                  }

                  if (/^\d*\.?\d*$/.test(val)) {
                    setAmount(val);
                  }
                }}
                value={amount}
              />
              <span
                className={`${
                  isBitramp() ? "text-[#CFD1DA]" : "text-[#222B45]"
                } absolute right-3 top-[2.5rem] -translate-y-1/2 text-[1rem] cursor-pointer`}
              >
                {selectedCurrency}
              </span>
              {!isAmountValid && amount !== "" && (
                <p className="w-full text-left" style={{ color: "red" }}>
                  Insuficient amount
                </p>
              )}
            </div>

            <div
              className={`${
                isBitramp()
                  ? "bg-[#2B2B2B] border border-[#464646] text-white focus:border-[#464646] focus:ring-0"
                  : "border border-gray-200"
              } w-[22rem] mt-4 rounded-[0.625rem] pb-3 pr-3`}
            >
              <div className="flex justify-between items-center mb-2">
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full text-left text-xl font-medium focus:border-none focus:outline-none bg-transparent border-none outline-none order-transparent focus:border-transparent focus:ring-0"
                  value={percentAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPercentAmount(Number(value));
                  }}
                />
                <span className="text-gray-400 font-semibold">
                  {selectedCurrency ? `${selectedCurrency}` : "N/A"}
                </span>
              </div>

              <div className="flex justify-end mt-2 space-x-4 text-sm font-[satoshi]">
                {[25, 50, 75, 100].map((percent) => (
                  <button
                    key={percent}
                    className={`${
                      selectedPercent === percent
                        ? //   ? "text-gray-900 font-semibold"
                          //   : "text-gray-400"
                          isBitramp()
                          ? "text-white"
                          : "text-gray-900 font-semibold"
                        : isBitramp()
                        ? "text-[#9F9F9F]"
                        : "text-gray-400"
                    } focus:outline-none`}
                    onClick={() => handlePercentChange(percent)}
                  >
                    {percent}%
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {QRCode ? (
          <button
            disabled={!isAmountValid}
            className={`${
              isBitramp()
                ? "Primary_button mb-[3rem] mt-[3rem] ml-[10rem] !w-[9.3125rem] !h-[2.8125rem]"
                : "text-[#F9F9F9] mb-[3rem] mt-[1rem] flex items-center justify-center mx-auto text-center text-[1.1875rem] font-[700] buttonModal"
            } `}
            onClick={() => {
              onClose();
              setQRCode(false);
            }}
          >
            Done
          </button>
        ) : (
          <button
            disabled={!isAmountValid}
            className={`${
              isBitramp()
                ? "Primary_button mb-[3rem] mt-[3rem] ml-[10rem] !w-[9.3125rem] !h-[2.8125rem]"
                : "text-[#F9F9F9] mb-[3rem] mt-[3rem] ml-[9.5rem] text-center text-[1.1875rem] font-[700] buttonModal"
            } `}
            onClick={() => {
              handleCryptoDeposit();
              setQRCode(true);
            }}
          >
            Send
          </button>
        )}
      </div>
    </div>
  );
};

export default DepositModal;
